<template>
  <PageWrap :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
    <template #left>
      <BaseAlert
        v-if="unleash.isEnabled('WebsiteService')"
        size="s"
        icon="pricetag"
        :text="t('website_service.banner.price_alert', { price })"
      />
      <BaseHeading size="xl">
        {{ t('website_service.banner.title') }}
      </BaseHeading>
      <BaseText>{{ t('website_service.banner.description') }}</BaseText>
      <BaseText size="s">
        {{ t('website_service.banner.free_consultation') }}
      </BaseText>
    </template>
    <template #cta>
      <BaseButton
        color="primary"
        @click="onJoinWaitingListClick"
        v-test="'website-service-waiting-list-button'"
        :loading="loading"
      >
        {{ t('website_service.banner.primary_action') }}
      </BaseButton>
    </template>
    <template #links>
      <DemoButton />
      <HelpPageButton />
    </template>
    <template #right>
      <img :src="'/img/online/website-service.svg'">
    </template>
    <template #bottom>
      <Flex
        :direction="$screen === 's' ? 'column' : 'row'"
        :gap="1.5"
        :class="$style.features"
      >
        <div
          v-for="feature in features"
          :key="feature.title"
          :class="$style.cardWrapper"
        >
          <BaseCard
            fullWidth
            fullHeight
          >
            <Flex
              direction="column"
              :gap="1"
            >
              <BaseIcon
                :name="feature.icon"
                color="warning"
                size="l"
              />
              <BaseText bold>{{ feature.title }}</BaseText>
              <BaseText color="secondary">{{ feature.description }}</BaseText>
            </Flex>
          </BaseCard>
        </div>
      </Flex>
    </template>
  </PageWrap>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandName } from '@/helpers/brand-name';
import PageWrap from '@/modules/online/PageWrap.vue';
import type { Website } from '@/types';
import unleash from '@/unleash';
import { CREATE_WEBSITE } from '../graphql';
import DemoButton from './DemoButton.vue';
import HelpPageButton from './HelpPageButton.vue';

const website = inject<Ref<Website | null>>('website');
const price = inject<Ref<string>>('price');

const { t } = useI18n();
const { mutate: createWebsite, loading } = useMutation(CREATE_WEBSITE, () => ({
  variables: {
    input: {},
  },
}));

const onJoinWaitingListClick = () => {
  createWebsite().then(({ data }) => {
    if (data?.createWebsite?.website) {
      website.value = data.createWebsite.website;
    }
  });
};

const features = [
  {
    icon: 'bulb',
    title: t('website_service.features.one.title'),
    description: t('website_service.features.one.description'),
  },
  {
    icon: 'sync',
    title: t('website_service.features.two.title'),
    description: t('website_service.features.two.description', brandName()),
  },
  {
    icon: 'search',
    title: t('website_service.features.three.title'),
    description: t('website_service.features.three.description'),
  },
  {
    icon: 'diamond',
    title: t('website_service.features.four.title'),
    description: t('website_service.features.four.description'),
  },
];
</script>

<style
  lang="scss"
  module
>
  .cardWrapper {
    flex: 1 1 0%;
    min-width: 0;
    border-radius: $radius;
    @include hover {
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
      transition: box-shadow 0.3s $easeInOutCubic;
    }

    .base:global(.v-enter-active) & {
      transition:
        transform 0.5s $easeOutCirc,
        opacity 0.2s linear;
      transition-delay: 0.3s;

      &:nth-of-type(1) {
        transition-delay: 0.3s;
      }
      &:nth-of-type(2) {
        transition-delay: 0.35s;
      }
      &:nth-of-type(3) {
        transition-delay: 0.4s;
      }
      &:nth-of-type(4) {
        transition-delay: 0.45s;
      }
    }

    .base:global(.v-enter-from) & {
      transform: translateY(50px);
      opacity: 0;
    }
  }
</style>
